import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, createSelector } from '@ngrx/store';
import { CreateCommentGQL } from 'graphql.generated';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';

import { generateMutationContext } from 'src/app/core/auth/session';
import { isDeltaBlank } from 'src/app/job/job-activity/comments.utils';
import { selectJobId, selectJobZone } from 'src/app/job/job-tool.reducer';
import { NewOpportunityActions } from 'src/app/new-opportunity/new-opportunity.actions';
import { selectJobPageChanges } from 'src/app/shared/unsaved-changes-banner/unsaved-changes-banner.selectors';
import { ActionSource } from 'src/app/state/action-sources';
import { generateUUID } from 'src/app/utilities/state.util';

import { NewOpportunityAddInventoryActions } from './new-opportunity-add-inventory.actions';
import { newOpportunityAddInventoryFeature } from './new-opportunity-add-inventory.feature';

export const buildUpdateInventoryVariables = createEffect((
    actions$ = inject(Actions),
    store = inject(Store),
) => {
    return actions$.pipe(
        ofType(
          NewOpportunityAddInventoryActions.addInventoryButtonClicked,
          NewOpportunityActions.nextStepButtonClicked,
          NewOpportunityActions.previousStepButtonClicked,
        ),
        concatLatestFrom(() => [
            store.select(newOpportunityAddInventoryFeature.selectInventoryText),
            store.select(selectJobId),
            store.select(selectJobZone),
            store.select(createSelector(
              selectJobPageChanges,
              (changes) => changes.filter((c) => c.source === ActionSource.ADD_INVENTORY).map((c) => c.id),
            )),
        ]),
        filter(([ _action, inventoryText, _jobId, _jobZone, changeIds ]) => !isDeltaBlank(inventoryText) && changeIds.length > 0),
        map(([_action, inventoryText, jobId, jobZone, changeIds]) => ({
            changeIds,
            vars: {
                input: {
                    id: generateUUID(),
                    threadId: generateUUID(),
                    contents: JSON.stringify(inventoryText),
                    text: '',
                    objectId: jobId,
                    objectType: 'Job',
                    mode: 'Update Inventory',
                },
            },
            opts: {
                context: generateMutationContext({
                    zone: jobZone.id,
                }),
            },
        })),
        map((payload) => NewOpportunityAddInventoryActions.buildUpdateInventoryVariablesSuccess(payload)),
    );
}, { functional: true, dispatch: true });

export const updateInventory = createEffect((
    actions$ = inject(Actions),
    createCommentGQL = inject(CreateCommentGQL),
) => {
    return actions$.pipe(
        ofType(NewOpportunityAddInventoryActions.buildUpdateInventoryVariablesSuccess),
        exhaustMap(({ vars, opts, changeIds }) => createCommentGQL.mutate(vars, opts).pipe(
            map(() => NewOpportunityAddInventoryActions.updateInventorySuccess({ changeIds })),
            catchError((error) => of(NewOpportunityAddInventoryActions.updateInventoryError({ error, changeIds }))),
        )),
    );
}, { functional: true, dispatch: true });
